import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { Inputs } from '../../services/household-retirement-service';
import { CLASS_LABELS, money, monthYear } from './format';

const BUCKET_LABELS: Record<string, string> = {
  rrsp: 'RRSP / RRIF', lira: 'LIRA / LIF', tfsa: 'TFSA / FHSA', non_registered: 'Non-registered', cash: 'Bank cash', unknown: 'Unknown',
};

/** What the plan read live from FolioWorth (read-only): the #31 investable
 * allocation with its DCA pool and reserves, tax buckets, home, RESP. */
export const InputsPanel: React.FC<{ inputs: Inputs }> = ({ inputs }) => {
  const m = (v: number) => money(v, inputs.currency);
  const invest = Object.entries(inputs.investable).filter(([, v]) => Math.abs(v) >= 1);
  const total = invest.reduce((s, [, v]) => s + v, 0);
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} data-testid="inputs-panel">
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Investable {m(total)} ({inputs.allocation_source === 'plan' ? 'investment plan allocation' : 'account balances'})
        </Typography>
        {invest.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <Typography key={k} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{CLASS_LABELS[k] ?? k}</span><span className="fw-num">{m(v)}</span>
          </Typography>
        ))}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          DCA pool {m(inputs.pool_remaining)} at {m(inputs.pool_monthly)}/mo
          {inputs.pool_target_end ? ` (done ~${monthYear(inputs.pool_target_end)})` : ''}; reserves {m(inputs.reserves)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">Tax buckets (account balances)</Typography>
        {Object.entries(inputs.buckets).filter(([, v]) => Math.abs(v) >= 1).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <Typography key={k} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{BUCKET_LABELS[k] ?? k}</span><span className="fw-num">{m(v)}</span>
          </Typography>
        ))}
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>RESP (held for education)</span><span className="fw-num">{m(inputs.resp)}</span>
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">Home and other</Typography>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Home</span><span className="fw-num">{m(inputs.home_value)}</span></Typography>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Vehicles</span><span className="fw-num">{m(inputs.vehicles)}</span></Typography>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Card balances</span><span className="fw-num">{m(inputs.other_liabilities)}</span></Typography>
        {inputs.notes.map((n) => (
          <Typography key={n} variant="caption" color="warning.main" component="div" sx={{ mt: 0.5 }}>{n}</Typography>
        ))}
      </Box>
    </Stack>
  );
};
